import type { PlayerCompareState } from "@livcg/schema";
import { RefreshCw } from "lucide-react";
import { Badge, Button, cn } from "../../../ui/index.js";
import {
  describeCompareError,
  describeCompareWarning,
  formatClock,
  statusLabel,
  statusTone,
} from "./playerCompareView.js";

type Props = {
  pc: PlayerCompareState;
  onRefresh: () => void;
  onSnapshot: () => void;
  snapshotBusy: boolean;
};

/** The result computed by the extension — «—» and errors are visible
 *  here BEFORE going on air. */
export function ComparePreview({ pc, onRefresh, onSnapshot, snapshotBusy }: Props) {
  const p = pc.payload;
  const warning = describeCompareWarning(pc.warning);
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border bg-surface p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Badge tone={statusTone(pc.status)} dot pulse={pc.status === "loading"}>
            {statusLabel(pc.status)}
          </Badge>
          {p && <span className="text-[11px] text-ink-faint">{formatClock(p.fetchedAt)}</span>}
        </div>
        <Button
          variant="secondary"
          size="xs"
          onClick={onRefresh}
          disabled={pc.status === "loading" || !pc.home || !pc.away}
          leadingIcon={<RefreshCw className="h-3 w-3" />}
        >
          Yangilash
        </Button>
      </div>
      {pc.status === "error" && (
        <p className="text-[12px] text-danger-strong">{describeCompareError(pc.error)}</p>
      )}
      {warning && <p className="text-[12px] text-warning">{warning}</p>}
      {p ? (
        <div className={cn("flex flex-col gap-1", pc.status === "loading" && "opacity-50")}>
          <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-muted">
            {p.title}
          </div>
          <table className="w-full text-[13px]">
            <tbody>
              {p.rows.map((r) => (
                <tr key={r.label} className="border-t border-divider">
                  <td className="w-24 py-1 text-right font-semibold tabular-nums">{r.home}</td>
                  <td className="py-1 text-center text-ink-muted">
                    {r.label}
                    {r.unit ? <span className="text-ink-faint"> {r.unit}</span> : null}
                  </td>
                  <td className="w-24 py-1 text-left font-semibold tabular-nums">{r.away}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-[12px] text-ink-faint">
          Ikkala o'yinchini tanlang — natija shu yerda ko'rinadi.
        </p>
      )}
      {pc.scene === "ht" && (
        <div className="flex items-center justify-between gap-2 border-t border-divider pt-2">
          <span className="text-[12px] text-ink-muted">
            1-taym snapshot:{" "}
            {pc.htSnapshot ? (
              <span className="text-ink">{formatClock(pc.htSnapshot.takenAt)}</span>
            ) : (
              <span className="text-warning">yo'q (faza halftime'ga o'tganda avto olinadi)</span>
            )}
          </span>
          <Button variant="secondary" size="xs" onClick={onSnapshot} disabled={snapshotBusy}>
            {pc.htSnapshot ? "Qayta olish" : "Snapshot olish"}
          </Button>
        </div>
      )}
    </div>
  );
}
